import CryptoJS from 'crypto-js'

class RemoteScreenManager {
// Private static properties
static #TARGET_KEY = '__remoteScreenSdk'
static #DEFAULT_KEY_STR = '0D804F1B55DE9E224D4DF0D68CB43F30'
static #DEFAULT_IV = '0000000000000000'
static #DEFAULT_WIDTH = 1570
static #DEFAULT_HEIGHT = 822

// Private static method to encrypt the content
static #encrypt(content, keyStr) {
keyStr = keyStr || this.#DEFAULT_KEY_STR
const key = CryptoJS.enc.Utf8.parse(keyStr)
const message = CryptoJS.enc.Utf8.parse(content)
const iv = CryptoJS.enc.Utf8.parse(this.#DEFAULT_IV)

const encrypted = CryptoJS.AES.encrypt(message, key, {
iv,
mode: CryptoJS.mode.CBC,
padding: CryptoJS.pad.Pkcs7
})
return encrypted.toString()
}

// Private static method to check if data is a valid JSON
static #isJSON(data) {
try {
return !!JSON.parse(data)
} catch (err) {
return false
}
}

// Private static method to calculate the center position of the window on the screen
static #calculateCenterPosition(width, height) {
const screenWidth = window.screen.width
const screenHeight = window.screen.height
return {
left: (screenWidth - width) / 2,
top: (screenHeight - height) / 2
}
}

// Private static method to validate required parameters
static #validateRequiredParams(params) {
const requiredParams = ['sn', 'token', 'refreshToken']
const missingParams = requiredParams.filter(param => !params[param])
if (missingParams.length > 0) {
    throw new Error(`Missing required parameters: ${missingParams.join(',')}`)
}
}   

// Constructor to initialize the RemoteScreenManager with configuration
constructor({
debug = false,
sn = '',
theme = 'light',
language = 'en',
tenantConfig = {themeConfig: {primary: '#22B3E8',}},
token = '',
tokenSaveTimestamp = +new Date(),
refreshToken = '',
targetUrl = 'https://www.skywayplatform.com/skyway-micro-modules/skyway-remote-screen.html'
} = {}) {
// Validate required parameters
RemoteScreenManager.#validateRequiredParams({ sn, token, refreshToken })
// Configuration settings
this.config = {
sn,
theme,
language,
tenantConfig,
token,
tokenSaveTimestamp,
refreshToken
}
// Other configuration settings
this.debug = debug
this.targetUrl = targetUrl
// Bind the message handler to the correct context
this.handleMessage = this.#handleMessage.bind(this)
}

// Public instance method to check if the window is initialized
isInit() {
return !!window[RemoteScreenManager.#TARGET_KEY]
}

// Public instance method to check if the window is open and not closed
isOpenWin() {
    return !!window[RemoteScreenManager.#TARGET_KEY] && !window[RemoteScreenManager.#TARGET_KEY].closed
}

// Public instance method to send a message to the remote screen
sendMessage({ type, data } = {}) {
if (!this.isInit()) return
window[RemoteScreenManager.#TARGET_KEY].postMessage(JSON.stringify({
type,
data,
from: 'oss'
}), '*')
}

// Public instance method to open the remote screen
open() {
if (this.isOpenWin()) {
window[RemoteScreenManager.#TARGET_KEY].focus()
return null
}

// Encrypt configuration parameters before sending
const encryptParams = RemoteScreenManager.#encrypt(JSON.stringify(this.config))

// Calculate window's center position on the screen
const { left, top } = RemoteScreenManager.#calculateCenterPosition(
RemoteScreenManager.#DEFAULT_WIDTH,
RemoteScreenManager.#DEFAULT_HEIGHT
)

// Build URL with encrypted parameters and optional debug flag
const urlParams = `params=${encodeURIComponent(encryptParams)}${this.debug ? '&debug=SEI' : ''}`

// Set window features like size and position
const windowFeatures = `width=${RemoteScreenManager.#DEFAULT_WIDTH},height=${RemoteScreenManager.#DEFAULT_HEIGHT},left=${left},top=${top},resizable=0`

// Open a new window with the target URL
window[RemoteScreenManager.#TARGET_KEY] = window.open(
`${this.targetUrl}?${urlParams}`,
'_blank',
windowFeatures
)

// Add message listener to handle communication with the remote screen
window.addEventListener('message', this.handleMessage)
return window[RemoteScreenManager.#TARGET_KEY]
}

// Public instance method to close the remote screen window
close() {
if (!this.isInit()) return

window[RemoteScreenManager.#TARGET_KEY].close()
window[RemoteScreenManager.#TARGET_KEY] = null
window.removeEventListener('message', this.handleMessage)
}

// Public instance method to update the configuration
updateConfig(newConfig = {}) {
this.config = {
...this.config,
...newConfig
}
}

// Private instance method to handle messages from the remote screen
#handleMessage(event) {
if (!RemoteScreenManager.#isJSON(event.data)) return // Ignore non-JSON messages
const { type, data, from } = JSON.parse(event.data) || {}
if (from !== 'remote-screen') return // Only handle messages from the remote screen

// Handle specific message types
switch (type) {
case 'ping':
console.log('ping')
this.sendMessage({
type: 'pong',
data
})
break
}
}
}

export default RemoteScreenManager

// https://www.skywayplatform.com/skyway-micro-modules/skyway-remote-screen.html?params=8oVZBeIt8%2B1JSKeKm2Zj2PXFATjYx8gPYehaajdCn7GJFbTqNPHfIdTF2Ow3Q7LuAy01N7Ey7ca%2Fvi4VS5Bg1Wtz1uvTwqn0ZTDGlSn2Q1PjPJA%2B3%2F7Au723v2ZpRQ4go3b0wPJTJp%2Fkv50ltyJH2aXmzI2HbKCqrDjb8c1tLBXViL27u3iux5dU%2FWzUFmPmUm64OeCRJgBZMbtqgfWVEN3GAhlUbMRnpMT95%2Be8M6h%2BsNUFGwDW8wlCbuJy5dWKCtxSIWb%2B7ebOK68YI%2FI%2FblqTI2XAk2ggArJp1nhwIFpwNXmXaTrJdFR7078tsLFQsrX9g9TnTtuIhzFrLsP0b%2FMjaF1HKE%2F7LVCymoeqhbBWGObqk0BeJCz8Aj5trDtsnMBiUsYcNGGXYtE0XlKgWQGgyi9LacUerUmWSWjE%2FmdcMI7FEUSREK9NRWiPjFgtMlZZ0l862iZSRFhJJJkl1%2BSfb9lZJA2hKSvBO7rdeHX2Ewx7eMy%2Fpu57qmqCZfET6%2BdE5Dn82kepiueXx5rzWE8fnLpzwF7TZBn7wdvagEtK4U4b0EOJ9p9B%2F654BIIoAaRVu1aAxqWibTaFvVUuN2AtvEYmyMRLlQuWAYop7Do26RyjQhgzvxSVtepeCeoCHAaJasG1nruPmOnGmtLKiXGSPpwT6bH6cyd9kEkD7clV0bW4vxPhayxdG4duiodzOLsypUvm5o4OzB%2FoGLfcDilCzOr%2FzAAkNYqIgCO67UXsgzdpObyg6wrHk%2FNPA%2FxH3hss7G1o1t1tWFMZzvW71P2VVT88Par2JJ2nujoHo7FD7rVaQfGGTi3FdK2wEPyxKcQp2LOKklyP%2Fbi%2Bc8YI3qXZccX8547Ip8io3Q6sPFSZwCDRYLi1uVGkyi01t9rXWbMRCIgK0HbcvWfNkX7aPWSQ1VZtcW1N5ASE92GqauwPEQyoIxyzT3PK7YxvIxy1z2PISxf4eaJDnG362YB9dOvkz%2B6Qx6lzKjxm1wb18Yuaa1ec%2BfYEc%2F6mMiLi5FZofYM3W21PMYOhbyZCkGafYnbtG%2BZBn1ccuXpKrIVS2iWK1OIDtC6IsoNx3I3nyG939bQOqWRMFVqB0h7U7bfM1LUDUu%2BtvVSPkSz42NuPhqp169kY924TUfxudRGdnig%2B939TQPExQedT6iCY920bH%2Fhx3j4kVsi%2Bh4G25G7j6D8ArYCbgg7pCqBjMqePEsbei7TwOv59M51a3r2bi48%2F1Y%2Fp6e7P35%2FXdcTyico0rpnh0rnyqMCXJFxgfwQi66bQFxtFCBNTeH38wDtJeotPGZ%2BDhEcIw3iwou0uTMcR9eZoEI670zFlDvuLDM7Wwyg9WrZRckk2N5kEvG0yFZs%2FZSke%2BnUfj4WvxH1OybfwHKuiL4KqVESuwVr9AK9ybdR6BDT5dWXHM7r8ejq1W6ZpqFIPFDJVIrtUybqbQvCGpADg6KmQzI4Fn75J7BXqd2R24SYxJCXQsLpKCiEPcyK8W%2F9DjFU%2B8sR5mPP8dODT2OObquZTxyBoJVovZbZ1asboGLIRr20P09tlkt6cUE384HZdSynRKM34DP6vz%2BXN8mwK8SLzfriZh%2F2QCu6Y2IMKBqDu4ZO%2F0j%2Fgiy2nDNQk1kHTV%2B%2Fnv9yfjPPPqRCnYMOMhYAJWfqH%2Bezr61kKl2qPC5T2jOef1t2PICShTO%2BFJbwBe%2FB3TnmHCNGlpQC3hWBxQLSYUTlhZ1oWnbIzcEt9DEwxXIpL%2F2E7cMs3EkwCYPsuAO%2FlFHXq9TzdDFAQLWF1re%2Fb8zh9hESJgFV5Z%2Fn7eANstBf88UDdUd59J7Rzru6Ap00UIb%2BNFpd6prgzL90yzKnsB5T0n6wMfKeOIFT5g7Rwj9tnNRQLph9p6P1sAnNJvJkARKk3EmgLCM4uTrLqTnRbeSLe3b7%2B9CJOgvNbR%2BGW1FPkcoTLSNLu%2FZfNKF%2Bb2egln%2FX8LhLDR1yxol5vYr0E7JhnWN%2BM62mHz%2FRAzungfPcpJNksulVHE%2F9W2NfAdVTdG%2FM5ivlOTgu45V8NiSpXm7hv4SzPQHOec5fK
// https://www.skywayplatform.com/skyway-micro-modules/skyway-remote-screen.html?params=8oVZBeIt8%2B1JSKeKm2Zj2PXFATjYx8gPYehaajdCn7GJFbTqNPHfIdTF2Ow3Q7LuAy01N7Ey7ca%2Fvi4VS5Bg1Wtz1uvTwqn0ZTDGlSn2Q1PjPJA%2B3%2F7Au723v2ZpRQ4go3b0wPJTJp%2Fkv50ltyJH2aXmzI2HbKCqrDjb8c1tLBWfnAfW6OhX5%2Bw09a737xgN%2FuZrrPQotW8Hw7EukIWICc7tUlO%2BVHRbEVvlUQEiuTvi5Vb9LbcbDPkhp%2FAdl%2BwWlbJhzbDSKQ169K2tUtFAarM2xLp25C6QQAEegA%2Bgbi88TimxPds2jRxAA1Dbb%2FakuX6w9jIV88GiQW73ROSuTE8LrkWMN3diyzGoaYqPoXoYp%2BCEULMHtfcEt7Giqn4czcf7N5hZ3guK%2F1jnM7xlmVBiWyd4KgyIoOy3FYA0OvQeoNVZzcpIPbiVkFQGEMz99MfLA0Xtq62VFWlAATg%2F4vsACmo0Vd3sSZKfXXbGgSP56JOrNlkUyIFZZGN6PvgLQFpzhSVL1nFdAwRmjXAHiulGprS0c%2BgAkmrmBmdTcqmQDHONGLEQAx2HktR1czv%2FXIpPKoKsKxryY6nvVuA5EvvwS%2FCYWfcfph8YuPhek%2B2cJQE7BTW7FbmSXWZRWjNUYQNQaXodg9tf39kFSDODLNiOMUNuewWbSgDJ9Zkbk8Kyrihu7bY59T1lz%2BmVj5dhrQBnvFCwq3Lwa8JCVGpYHkAd%2F67bs%2FDuUjGMJy9%2FBU%2BJjjfJ4BMn5sXnCCNawESzajo7toALlc9V91nPM4%2BKoHq5pfxStIsA%2BnXt4TR%2ByrfxAOIOehejeIojNx4rUhKoMaLs3oCUVDNamkFJ%2B6xKM5WLd6qlkwrEsUn1aIu2va%2FyC8CGrf2mBlFVmvmdnd%2F2wmqHj3%2FS5E0BN6YBBLu4JFaLwgMwrwLhjQ%2BG7eV9SnoqCufHkYtsNl1ASbDNexvxppeTveoRwBGHfIAyBtmRDWAFYJHLd4D96VCmwERD6ZVPFmwKdiScQL2yQ1OUtzF4RRionNy3njtEDNtrzsF67pzWWCR%2BClR%2B0Yu6Af9VxkMXvTy%2BnzPf9dnBMvXyB0%2FNRnQnXapMvzvKgFq4d%2BhKfUIV0WAPzJXTpUhoaB0cA%2BoowpCM%2FUKphD7Bxlwy2R5mgjxHJKN54OoJQevo2aWBCEO%2B3k0Pbfu3PqUqgeVFGTfDSAiTsxNTjbpmK4Ja8HSI5A2QAw7dJJuYASUj82HVcCo858y%2Fe1FnXH4maoPXNCf4YN4RLjnps5n6kNaPmrLtHjKR8taFWFyQXLevSCin26tTRtWCy8rkTGKFQfBWnHidZvdMAzWwSDK31ynQz%2FhxPvB4c%2B9GAVTR9ZqHfG2Bwm9I09vt4WAr0PGxswSpgROKVDK634V5mVlodazsSUKD7Fv8LLZftA3jWZKFzIooNfnjNAS2KB2sHMWV2m%2FKvx8E1VsSvsFPCaBtGliFhFwm8krhsLlAuPrCjEoWYXUHc3Y%2F5NPtummylikHmGxYeNfYkXz1CaW%2Fj5uevVLlv7%2BpYu4I2QQmAXcORND8DgVXP%2BknLpFyz0qSt7EzwByHlwLDne4mFqou0lzfIdxs%2BWOIkpbg%2FQMImXKxjLbxifIzhcKEouBa%2FgRDyevjMxODObjwL4CfcDlt2W%2FI2K9QbLaCzX4yqd7cGDJPTzqe8AxKqZTu8wNBtxhVgO51r3vfdnB0gfDo0XecinylmkX9wjL2FXfEAJWsEKm%2FjqzcZmqHXL7b8iiEshItR7UTNlG354cLbUrwKkzClw4gBKEDw39fG8f%2FFZLmQ0xPMkKiqwrrhEQDKbyOsCHzJEnx70%2FgTHiJcj1NS8Yme3suLEiUgGFntaUFWOxopRl4bW0G9hqX77OwhgMUDMNie9WJCPE1468nL32LmgmkFgW7z1lEJM%2F%2BohuUHG0dwJK4SXcMWhVFIxfGZyNOcgJqUNj5skxYqS9j371QahCGiwGbDXfT3DoStIR2aneT53MiZLdpDbfvGbytXJc%2FDOHDmhF8sjMiMFDzUNUGpTGzenvB1bGxC0Pt
// https://www.skywayplatform.com/skyway-micro-modules/skyway-remote-screen.html?params=8oVZBeIt8%2B1JSKeKm2Zj2PXFATjYx8gPYehaajdCn7GJFbTqNPHfIdTF2Ow3Q7LuAy01N7Ey7ca%2Fvi4VS5Bg1bg9Tns1d2i6Brmo97OIQOwdiY9hSoa4MNzFNxNEN0OHTaHx706zu0SKF5hASgeNMeVLRIKuG2KbDxiMU4BNB94GH4EsO2G8cbwl98v32TJBIG8lt%2FhU4crNy9iNlCVEvjH%2FFJPVsh6uaMDNxXMMdmlj4GFtpBVjIdrs2xQwwgmOXCmeoaHHfucDc9o6Ecc3jNO%2FBV%2BFwnbK3a0Llk7X28A%2FMq%2FGUvitxP8bDOZX3JQsEKyuyWihWMVuqbpWLuRLHuipsbpjKeqUtfbYneMC2vR%2Bl6uTdy7Sz0qEw23C3g%2Bqd5l2gBm56a5QmRNXTiDdfyGSBDzzFK%2B6hZehwPxOZG7kIIfQP6hIv9P8R7h61mttPwCC0IJeyYvGJXHv7id380BF9qb5Okl9O0musS4VQKWmtlz%2FYNq1vLuRNCkjyoNuf42C3PVQbwCFdKdFheCVnE0JjEu0B4I9TN3OvJXREJoHceyvSnA2P7UMNtyRzZpMljrJ0TYDNPIlb0bGGvcWVn%2F6czNmK295gr8RQBuKbdCZhREb1OozSBRjrm2IITD%2BAjgguZf0iwCTGE%2FWvaqtecS3HfOieeqJWu2k%2FP8Fvs3yZUEzSnZ3Q6b%2FSYv7X5s%2BPuFfv8OUUvxI8WbYzdqC%2F3WJBBM7joU74w3LzHyCYI%2FXZdqo5cHEA7euM6tcf%2Brb0Cc5uZ79nkfo1ncKFW66ETaQ7Hjmxq7GqZnIQDv6kI3qNR4SLdbgCSzzzv79UKMnXXI4QsJItRta19kt%2F62bS4N1xYN9tY8T08ZCmlEsIuqNcwI4IZdMSchO%2FPe3STHPKfA2FACFyQBswX5nF1oSXYrTQ84kY9roIHQKWOZbX%2BpNDGHmgQKrFmrU%2FPZvtXo7rapAslkHj1LrxPD7WY6o0lovtQ%2FdhBval%2BZklekB77bgrqWlJ2MC0FjnVUA91T2zZE2HlHZZl%2F6Q0Dp3dU21%2FLVkre7dUvyW1aHiZ0V7Dwp1gQUEL4NN%2FRATq0zaIu7kzte8FXXYbL5m0EBB3PaN2TOBT%2F2lfvslBbQ8XMQs%2FkQCAL6S9PZVeA1SzoMNttdvsfwxt4CVraFUGKbzS0UH2YCubqLjE4aWS9KxO0v1DrKe0o7NS657VOAhUUezpURquL36k%2BATA5k52lAY1OyfBRdFX%2B8dS%2FyF7JDEp2gt7iURZKwukVvp85cpDC61jX%2B7Aa8eufD97AWqjL4EhffvLXpQ4YcLfhIrKKJUGF7BbYylS4mIMhOQi8sbF5IUpEpzUquwbpSqwcC3Q4mfmW6LSMty4710B3A563B5o77HyHDBczwfgVho0y8MIPg1AVyFGXo8KmHHgVPmDSFThkT8%2FbzSj0L1ll5itfBFMm7OkrLhlrVnllHZvs5Gezqeax1UiTxtmlbcsa5PspLTC%2ByG5srg5K%2BNowKm%2BkMAt922CpaH%2FAAgmB64O2yNyV7z2gPWkhcMo767CieZ2RwcgLjVoBZMmOYJOVsF4AdtEXXOJW%2BklMOtmFDRb7rXoj%2Fwk8wB8QBL7ptusX3of1di9LnuNafUAwmB5xOyhcz30s7m1e6WAK0XpweilDkzinC5lorfpTF%2FelXEg2p0PZdX9uYG%2Fh7etH2SFTwkb5reGjvPzB%2FUF0zYT69BH41zmyuoz3NsXBieyDKkTTh1jnm45w4e4Ln1SdGJ9DdE9SySOyQ1tEDo8nCMMG6ntAKF%2BBGmoFy2SbQPqTL5vxzRRYdo1hIYxLYgp9aBUM5dN7k8%2BIkQnjRWmBNYzEJdMhakRkmO56jjccA5sMAsQIYCd9mNdUHJxOMEtGF3cSpRY6lIfYpadvElEDYF%2BKtg3SaXDy9FizmrtkLKVHZ2Pm0PrQiTY1M%2Fp%2BpaCOjH5EFguoGmfIJRSCFE7Zof1gDGsjNrf967ocYUGeaSV5PfL9I5XP0DKkKouCe04CZVIHY%2FCgEclu4Ueb8oZtjd09ta3bTRZTA6HwPV7SQ2Jng4ocGhRXcoBKdMe7CfLbGtgDMdXeGOBK9%2F1oo%3D